// Page: EnterpriseSignUp
// Purpose: Enterprise-specific signup that validates email domain, sends email
// verification, assigns the new user to the enterprise, and routes to verify.
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthLoadingScreen from '../../../components/shared/AuthLoadingScreen';
import { ErrorMessage } from '../../../components/ui/feedback/error-message.jsx';
import SignUpForm from '../../../components/auth/signup/SignUpForm';
import api from '../../../api';
import SEOHead from '../../../components/shared/SEOHead';
import Footer from '../../../components/shared/Footer';

export default function EnterpriseSignUp() {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp } = useAuth();
  const isDark = useDarkMode();
  
  // Get enterprise data from navigation state
  const enterprise = location.state?.enterprise;
  const domain = location.state?.domain;

  // Redirect if no enterprise data
  useEffect(() => {
    if (!enterprise || !domain) {
      navigate('/signup', { replace: true });
    }
  }, [enterprise, domain, navigate]);

  const toggleTheme = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  const handleInputChange = () => {
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const formData = new FormData(e.target);
    const email = formData.get('email');
    const password = formData.get('password');
    const confirmPassword = formData.get('confirm-password');
    const firstName = formData.get('first-name');
    const lastName = formData.get('last-name');
    // Capture phone and role like regular signup does (hidden inputs from PhoneInput)
    const phoneE164 = formData.get('phone_e164') || '';
    const phoneCountry = formData.get('phone_country') || '';
    const phoneNational = formData.get('phone_national') || '';
    const role = (formData.get('role') || '').trim();

    // Input validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!firstName || !lastName) {
      setError('First name and last name are required');
      return;
    }

    // Validate email domain matches enterprise domain
    const emailDomain = email.split('@')[1]?.toLowerCase();
    if (emailDomain !== domain.toLowerCase()) {
      setError(`Email must be from ${domain} domain for this enterprise plan`);
      return;
    }

    setIsLoading(true);

    try {
      // Check eligibility for enterprise (including account limits)
      console.log('Checking enterprise eligibility...');
      try {
        const eligibilityResponse = await api.post('/enterprise/check-eligibility', {
          email,
          domain,
          enterpriseId: enterprise.id
        });

        console.log('Eligibility response:', eligibilityResponse);

        if (!eligibilityResponse.eligible) {
          const reason = eligibilityResponse.reason || 'You are not eligible for this enterprise plan.';
          setError(reason);
          setIsLoading(false);
          return;
        }
        
        console.log('Eligibility check passed, proceeding with signup...');
      } catch (eligibilityError) {
        console.error('Error checking enterprise eligibility:', eligibilityError);
        if (eligibilityError.response?.data?.error) {
          setError(eligibilityError.response.data.error);
        } else {
          setError('Error checking enterprise eligibility. Please try again.');
        }
        setIsLoading(false);
        return;
      }

      // Create account with enterprise plan level (domain validation will happen server-side)
      console.log('Creating user account...');
      // Pass phone, role, and hasSeenContactInfoModal so we don't re-prompt later
      const extraProfile = {
        phone: {
          e164: phoneE164,
          national: phoneNational,
          country: phoneCountry
        },
        role,
        hasSeenContactInfoModal: true
      };
      const user = await signUp(email, password, firstName, lastName, 'basic', false, extraProfile);
      // Prefer the live Firebase user; signUp may return profile shape without nested user
      const { auth } = await import('../../../firebase/firebase');
      const currentUser = auth.currentUser;
      const effectiveUid = (currentUser && currentUser.uid) || user?.uid;
      const effectiveEmail = (currentUser && currentUser.email) || user?.email || email;
      if (!effectiveUid) {
        console.warn('Signup returned without a user object, but Firebase auth user unavailable');
      }
      console.log('User account created successfully:', effectiveUid);

      // CRITICAL: Send email verification IMMEDIATELY after user creation
      // This must happen before any profile operations that might clear auth state
      console.log('Sending email verification immediately...');
      try {
        // Import Firebase modules
        const { sendEmailVerification } = await import('firebase/auth');
        
        // Get the current Firebase user (should exist immediately after signup)
        if (currentUser && effectiveUid && currentUser.uid === effectiveUid) {
          // Configure email verification with custom action URL
          const actionCodeSettings = {
            url: `${window.location.origin}/verify-email`,
            handleCodeInApp: false,
          };
          
          await sendEmailVerification(currentUser, actionCodeSettings);
          console.log('Email verification sent successfully to:', currentUser.email);
        } else {
          console.error('No current user found or UID mismatch for email verification');
          throw new Error('Authentication state mismatch during verification email sending');
        }
      } catch (verificationError) {
        console.error('Error sending verification email:', verificationError);
        // Don't fail the entire signup process - user can resend verification email later
        console.log('Continuing with signup flow despite verification email error');
      }

      // Wait a moment for user profile to be fully created in database
      console.log('Waiting for user profile creation to complete...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Assign user to enterprise using the signup endpoint (doesn't require auth)
      console.log('Assigning user to enterprise...');
      try {
        await api.post('/enterprise/signup-assign-user', {
          userId: effectiveUid,
          email: effectiveEmail,
          enterpriseId: enterprise.id
        });
        console.log('User assigned to enterprise successfully');
      } catch (assignmentError) {
        console.error('Error assigning user to enterprise:', assignmentError);
        
        // This is critical - if enterprise assignment fails, we should not proceed
        // The user would be created but not properly assigned to the enterprise
        let assignmentErrorMessage = 'Failed to assign your account to the enterprise. ';
        
        if (assignmentError.response?.data?.error) {
          assignmentErrorMessage += assignmentError.response.data.error;
        } else {
          assignmentErrorMessage += 'Please contact support@advocacylab.io for assistance.';
        }
        
        setError(assignmentErrorMessage);
        setIsLoading(false);
        setIsRedirecting(false);
        return; // Don't continue to verification if enterprise assignment failed
      }

      console.log('Enterprise signup successful - redirecting to email verification');
      
      // Redirect to email verification page
      navigate('/verify-email', { 
        replace: true,
        state: { 
          enterprise,
          isEnterprise: true,
          userEmail: email,
          userId: user.uid
        }
      });
      
    } catch (err) {
      console.error('Enterprise signup error:', err);
      let errorMessage = 'Failed to create enterprise account. Please try again.';

      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please try signing in instead.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format';
      } else if (err.message && typeof err.message === 'string') {
        if (!err.message.toLowerCase().includes('network error')) {
          errorMessage = err.message;
        }
      }

      setIsLoading(false);
      setIsRedirecting(false);
      setError(errorMessage);
      
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  if (isRedirecting) {
    return <AuthLoadingScreen />;
  }

  if (!enterprise) {
    return <AuthLoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      <SEOHead pageType="enterprise-signup" />
      
      {/* Landing header with toggle theme button */}
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        backLink="/signup"
        backButtonText="Back to Sign Up"
        showLogo={false}
        enterpriseLogo={enterprise.enterpriseImageUrl || enterprise.enterpriseImage}
        enterpriseName={enterprise.enterpriseName}
      />
      
      {/* Content section */}
      <div className="bg-white dark:bg-gray-900 px-4 py-12 w-full">
        <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8">
          <div className="text-center">            
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400">
              Join {enterprise.enterpriseName}
            </h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight max-w-[90%] md:max-w-full mx-auto">
              Create your enterprise account
            </p>
            <p className="mt-4 text-lg text-gray-700 dark:text-gray-300 max-w-[90%] md:max-w-full mx-auto">
              Access your organization's {enterprise.planType} plan features.
            </p>
          </div>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="mx-auto mb-6 w-[95%] md:w-[85%] max-w-[600px]">
            <ErrorMessage message={error} />
          </div>
        )}
        
        {/* Email/Password Sign Up Form */}
        <SignUpForm 
          onSubmit={handleSubmit} 
          isLoading={isLoading}
          onInputChange={handleInputChange}
          emailPlaceholder={`your-email@${domain}`}
        />

        {/* Link to enterprise sign in */}
        <div className="mx-auto w-[95%] md:w-[85%] max-w-[600px] mt-8">
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Already have an account?
            </p>
            <Link 
              to="/enterprise/signin" 
              state={{ enterprise, domain }}
              className="mt-1 inline-block font-medium text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 hover:underline"
              onClick={handleInputChange}
            >
              Sign in to {enterprise.enterpriseName}
            </Link>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
} 